import { useState } from "react";
import { api, formatApiError, shopLogoUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronsUpDown, Check, Store, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

export function ShopSwitcher() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!user || user.role !== "owner") return null;
  const shops = user.shops || [];
  if (shops.length < 2) return null;

  const active = user.shop_id;
  const label = active === "all"
    ? "Todas"
    : (shops.find((s) => s.id === active)?.name || "Unidade");

  const pick = async (id) => {
    if (id === active) { setOpen(false); return; }
    setBusy(true);
    try {
      await api.post("/auth/switch-shop", { shop_id: id });
      await refresh?.();
      toast.success(id === "all" ? "Vendo todas as unidades" : "Unidade trocada");
      setOpen(false);
      // Reload so lists and dashboards pick up the new scope.
      setTimeout(() => window.location.reload(), 150);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="shop-switcher-btn" type="button"
          className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-md border border-zinc-800 bg-zinc-900 text-xs text-zinc-100 font-mono-caps uppercase hover:bg-zinc-800 max-w-[128px] sm:max-w-[180px]"
        >
          <Store className="h-4 w-4 shrink-0" style={{ color: "var(--c-brand)" }} />
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid="shop-switcher-menu" align="end" className="p-0 w-64 bg-zinc-950 border-zinc-800 text-zinc-100">
        <div className="py-1 max-h-[60vh] overflow-y-auto">
          {shops.map((s) => (
            <button key={s.id} data-testid={`shop-switcher-item-${s.id}`} disabled={busy}
              onClick={() => pick(s.id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-zinc-900 text-left disabled:opacity-50"
            >
              {s.logo_path ? (
                <img src={shopLogoUrl(s.id)} alt="" className="h-8 w-8 rounded-md object-cover border border-zinc-800 bg-zinc-900" />
              ) : (
                <span className="h-8 w-8 rounded-md bg-zinc-900 border border-zinc-800 inline-flex items-center justify-center">
                  <Store className="h-4 w-4 text-zinc-500" />
                </span>
              )}
              <span className="flex-1 truncate">{s.name}</span>
              {active === s.id && <Check className="h-4 w-4" style={{ color: "var(--c-brand)" }} />}
            </button>
          ))}
          <button data-testid="shop-switcher-item-all" disabled={busy} onClick={() => pick("all")}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-zinc-900 text-left border-t border-zinc-900 disabled:opacity-50"
          >
            <span className="h-8 w-8 rounded-md bg-zinc-900 border border-zinc-800 inline-flex items-center justify-center">
              <Store className="h-4 w-4" style={{ color: "var(--c-brand)" }} />
            </span>
            <span className="flex-1 truncate">Todas as unidades</span>
            {active === "all" && <Check className="h-4 w-4" style={{ color: "var(--c-brand)" }} />}
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" />}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
